import type { Order, Gift, TimeSlot, SantaBooking } from './types'

const API_BASE = '/api'

// Helper to get auth header
const getAuthHeader = (): Record<string, string> => {
  const token = localStorage.getItem('admin_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// Authed admin requests: a 401 means the session is gone, so send the volunteer back to login.
const adminFetch = async (input: string, init?: RequestInit) => {
  const response = await fetch(input, init)
  if (response.status === 401) {
    localStorage.removeItem('admin_token')
    window.location.assign('/admin/login')
  }
  return response
}

export const api = {
  // Admin authentication
  async adminLogin(password: string) {
    const response = await fetch(`${API_BASE}/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Login failed')
    }

    return response.json() as Promise<{ token: string; success: boolean }>
  },

  // Orders
  async createOrder(data: { email: string; fullName: string; phoneNumber?: string; wantsGifts: boolean }) {
    const response = await fetch(`${API_BASE}/orders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    return response.json() as Promise<Order>
  },

  async getOrder(id: string) {
    const response = await fetch(`${API_BASE}/orders/${id}`)
    if (!response.ok) {
      throw new Error('Failed to fetch order')
    }
    return response.json() as Promise<Order>
  },

  // Gifts
  async addGift(orderId: string, data: {
    pickupLocation: string
    recipientType: string
    recipientName: string
    fromName: string
    yearGroup: string
  }) {
    const response = await fetch(`${API_BASE}/orders/${orderId}/gifts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    return response.json() as Promise<Gift>
  },

  async deleteGift(orderId: string, giftId: string) {
    const response = await fetch(`${API_BASE}/orders/${orderId}/gifts/${giftId}`, {
      method: 'DELETE',
    })
    if (!response.ok) {
      throw new Error('Failed to delete gift')
    }
    return response.json()
  },

  async updatePickupLocation(orderId: string, pickupLocation: string) {
    const response = await fetch(`${API_BASE}/orders/${orderId}/pickup-location`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pickupLocation }),
    })
    if (!response.ok) {
      throw new Error('Failed to update pickup location')
    }
    return response.json()
  },

  async updateTokenQuantity(orderId: string, tokenQuantity: number) {
    const response = await fetch(`${API_BASE}/orders/${orderId}/tokens`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tokenQuantity }),
    })
    if (!response.ok) {
      throw new Error('Failed to update token quantity')
    }
    return response.json()
  },

  // Time Slots
  async getAvailableSlots() {
    const response = await fetch(`${API_BASE}/slots/available`)
    return response.json() as Promise<TimeSlot[]>
  },

  async getSlots() {
    const response = await fetch(`${API_BASE}/slots`)
    if (!response.ok) throw new Error('Failed to load slots')
    return response.json() as Promise<Pick<TimeSlot, 'id' | 'time' | 'slotNumber' | 'status'>[]>
  },

  async getSlotById(id: string) {
    const response = await fetch(`${API_BASE}/slots/${id}`)
    if (!response.ok) {
      throw new Error('Slot not found')
    }
    return response.json() as Promise<TimeSlot>
  },

  async lockSlot(id: string, orderId: string) {
    const response = await fetch(`${API_BASE}/slots/${id}/lock`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderId }),
    })
    if (!response.ok) throw new Error('Slot not available')
    return response.json() as Promise<TimeSlot>
  },

  async releaseSlot(slotId: string, orderId: string) {
    const response = await fetch(`${API_BASE}/slots/${slotId}/release`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderId }),
    })
    if (!response.ok) {
      throw Object.assign(new Error('Failed to release slot'), { status: response.status })
    }
    return response.json() as Promise<TimeSlot>
  },

  // Santa Bookings
  async createBooking(data: {
    orderId: string
    timeSlotId: string
    childrenNames: string[]
    dietaryRequirements: string
  }) {
    const response = await fetch(`${API_BASE}/bookings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw Object.assign(new Error(error.error || 'Failed to create booking'), { status: response.status })
    }
    return response.json() as Promise<SantaBooking & { lockedUntil: string }>
  },

  // Payment
  async createCheckout(orderId: string) {
    const response = await fetch(`${API_BASE}/payment/create-checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderId }),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Failed to create checkout')
    }

    return response.json() as Promise<{ checkoutId: string; amount: number; lockedUntil: string | null }>
  },

  async verifyPayment(orderId: string, checkoutId: string) {
    const response = await fetch(`${API_BASE}/payment/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderId, checkoutId }),
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw Object.assign(new Error(error.error || 'Failed to verify payment'), { status: error.status })
    }
    return response.json() as Promise<Order>
  },

  // Admin (protected endpoints)
  async getAdminOrders(search?: string) {
    const url = new URL(`${API_BASE}/admin/orders`, window.location.origin)
    if (search && search.trim()) {
      url.searchParams.set('search', search.trim())
    }
    const response = await adminFetch(url.toString(), {
      headers: getAuthHeader(),
    })

    if (!response.ok) {
      throw new Error('Failed to fetch orders')
    }

    return response.json() as Promise<Order[]>
  },

  async getAdminBookings() {
    const response = await adminFetch(`${API_BASE}/admin/bookings`, {
      headers: getAuthHeader(),
    })

    if (!response.ok) {
      throw new Error('Failed to fetch bookings')
    }

    return response.json() as Promise<SantaBooking[]>
  },

  async getAdminSlots() {
    const response = await adminFetch(`${API_BASE}/admin/slots`, {
      headers: getAuthHeader(),
    })

    if (!response.ok) {
      throw new Error('Failed to fetch slots')
    }

    return response.json() as Promise<TimeSlot[]>
  },

  async getAdminLabels() {
    const response = await adminFetch(`${API_BASE}/admin/labels`, {
      headers: getAuthHeader(),
    })

    if (!response.ok) {
      throw new Error('Failed to fetch labels')
    }

    return response.json() as Promise<Order[]>
  },

  // Settings
  async getAcceptOrdersSetting() {
    const response = await fetch(`${API_BASE}/settings/accept-orders`)
    if (!response.ok) {
      throw new Error('Failed to fetch settings')
    }
    return response.json() as Promise<{ acceptOrders: boolean }>
  },

  async getEventDate() {
    const response = await fetch(`${API_BASE}/settings/event-date`)
    if (!response.ok) {
      throw new Error('Failed to fetch event date')
    }
    return response.json() as Promise<{ eventDate: string | null }>
  },

  async getAdminSettings() {
    const response = await adminFetch(`${API_BASE}/admin/settings`, {
      headers: getAuthHeader(),
    })
    if (!response.ok) {
      throw new Error('Failed to fetch settings')
    }
    return response.json() as Promise<Array<{ id: string; key: string; value: string; updatedAt: Date }>>
  },

  async updateAdminSetting(key: string, value: string) {
    const response = await adminFetch(`${API_BASE}/admin/settings/${key}`, {
      method: 'PUT',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ value }),
    })
    if (!response.ok) {
      throw new Error('Failed to update setting')
    }
    return response.json()
  },

  // Data options
  async clearAllOrders() {
    const response = await adminFetch(`${API_BASE}/admin/orders/all`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    })
    if (!response.ok) {
      throw new Error('Failed to clear all orders')
    }
    return response.json() as Promise<{ success: boolean; count: number }>
  },

  async resetAllTimeslots() {
    const response = await adminFetch(`${API_BASE}/admin/timeslots/reset`, {
      method: 'POST',
      headers: getAuthHeader(),
    })
    if (!response.ok) {
      throw new Error('Failed to reset timeslots')
    }
    return response.json() as Promise<{ success: boolean; count: number }>
  },

  // Order editing
  async getAdminOrder(id: string) {
    const response = await adminFetch(`${API_BASE}/admin/orders/${id}`, {
      headers: getAuthHeader(),
    })
    if (!response.ok) {
      throw new Error('Failed to fetch order')
    }
    return response.json() as Promise<Order>
  },

  async updateAdminOrder(id: string, data: { email: string; fullName: string; wantsGifts: boolean; wantsSanta: boolean }) {
    const response = await adminFetch(`${API_BASE}/admin/orders/${id}`, {
      method: 'PUT',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
    if (!response.ok) {
      throw new Error('Failed to update order')
    }
    return response.json() as Promise<Order>
  },

  async deleteAdminOrder(id: string) {
    const response = await adminFetch(`${API_BASE}/admin/orders/${id}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    })
    if (!response.ok) {
      throw new Error('Failed to delete order')
    }
    return response.json() as Promise<{ success: boolean; message: string }>
  },

  async updateAdminOrderNotes(id: string, notes: string) {
    const response = await adminFetch(`${API_BASE}/admin/orders/${id}/notes`, {
      method: 'PUT',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ notes }),
    })
    if (!response.ok) {
      throw new Error('Failed to update order notes')
    }
    return response.json() as Promise<Order>
  },

  async updateAdminGift(id: string, data: { recipientType: string; recipientName: string; fromName: string; yearGroup: string; pickupLocation: string }) {
    const response = await adminFetch(`${API_BASE}/admin/gifts/${id}`, {
      method: 'PUT',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
    if (!response.ok) {
      throw new Error('Failed to update gift')
    }
    return response.json() as Promise<Gift>
  },

  async deleteAdminGift(id: string) {
    const response = await adminFetch(`${API_BASE}/admin/gifts/${id}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    })
    if (!response.ok) {
      throw new Error('Failed to delete gift')
    }
    return response.json()
  },

  async updateAdminSantaBooking(id: string, data: { childrenNames: string[]; dietaryRequirements: string }) {
    const response = await adminFetch(`${API_BASE}/admin/santa-bookings/${id}`, {
      method: 'PUT',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
    if (!response.ok) {
      throw new Error('Failed to update Santa booking')
    }
    return response.json() as Promise<SantaBooking>
  },

  // Pricing
  async getPricing() {
    const response = await fetch(`${API_BASE}/pricing`)
    if (!response.ok) {
      throw new Error('Failed to fetch pricing')
    }
    return response.json() as Promise<Record<string, number>>
  },

  async getAdminPricing() {
    const response = await adminFetch(`${API_BASE}/admin/pricing`, {
      headers: getAuthHeader(),
    })
    if (!response.ok) {
      throw new Error('Failed to fetch pricing')
    }
    return response.json() as Promise<Array<{ id: string; itemType: string; price: number }>>
  },

  async updateAdminPricing(id: string, price: number) {
    const response = await adminFetch(`${API_BASE}/admin/pricing/${id}`, {
      method: 'PUT',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ price }),
    })
    if (!response.ok) {
      throw new Error('Failed to update pricing')
    }
    return response.json()
  },

  // Gift picker
  async getGiftPickerOrders() {
    const response = await adminFetch(`${API_BASE}/admin/gift-picker`, {
      headers: getAuthHeader(),
    })
    if (!response.ok) {
      throw new Error('Failed to fetch gift picker orders')
    }
    return response.json() as Promise<Order[]>
  },

  async markGiftPicked(id: string, isPicked: boolean) {
    const response = await adminFetch(`${API_BASE}/admin/gifts/${id}/pick`, {
      method: 'PUT',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ isPicked }),
    })
    if (!response.ok) {
      throw new Error('Failed to update gift picked status')
    }
    return response.json() as Promise<Gift>
  },

  // Re-check unsettled checkouts against SumUp (lost webhooks)
  async reconcileOrders() {
    const response = await adminFetch(`${API_BASE}/admin/orders/reconcile`, {
      method: 'POST',
      headers: getAuthHeader(),
    })
    if (!response.ok) throw new Error('Reconcile failed')
    return response.json() as Promise<{ checked: number; completed: number; errors: string[] }>
  },

  // Manually mark order as paid (for cash/alternative payments)
  async markOrderPaid(orderId: string) {
    const response = await adminFetch(`${API_BASE}/admin/orders/${orderId}/mark-paid`, {
      method: 'POST',
      headers: getAuthHeader(),
    })
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Failed to mark order as paid')
    }
    return response.json() as Promise<{ success: boolean; order: Order; receiptId: string }>
  },
}
